import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, HeartPulse, DollarSign, Users, ShieldPlus, Search, Building2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_TYPES = ["health", "dental", "vision", "retirement", "life", "disability", "other"];
const TYPE_COLOR = {
  health: "bg-red-500/10 text-red-600 border-red-500/30",
  dental: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  vision: "bg-purple-500/10 text-purple-600 border-purple-500/30",
  retirement: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  life: "bg-pink-500/10 text-pink-600 border-pink-500/30",
  disability: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  other: "bg-slate-500/10 text-slate-600 border-slate-500/30",
};

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-2xl font-bold">{value}</p></div>
      </CardContent>
    </Card>
  );
}

export default function Benefits() {
  const [plans, setPlans] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState("plans");
  const [search, setSearch] = useState("");

  const [planOpen, setPlanOpen] = useState(false);
  const [planEdit, setPlanEdit] = useState(null);
  const emptyPlan = { name: "", plan_type: "health", provider: "", monthly_cost: 0, employer_contribution: 0, description: "", coverage_details: "", is_active: true };
  const [planForm, setPlanForm] = useState(emptyPlan);

  const [enrOpen, setEnrOpen] = useState(false);
  const [enrEdit, setEnrEdit] = useState(null);
  const emptyEnr = { employee_id: "", plan_id: "", effective_date: "", end_date: "", dependents: 0, status: "active", notes: "" };
  const [enrForm, setEnrForm] = useState(emptyEnr);

  const load = async () => {
    try {
      const [pRes, eRes, sRes, empRes] = await Promise.all([
        axios.get(`${API}/benefits/plans`),
        axios.get(`${API}/benefits/enrollments`),
        axios.get(`${API}/benefits/stats`),
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
      ]);
      setPlans(pRes.data || []); setEnrollments(eRes.data || []); setStats(sRes.data || {}); setEmployees(empRes.data || []);
    } catch { toast.error("Failed to load benefits"); }
  };
  useEffect(() => { load(); }, []);

  const empName = (id) => employees.find((x) => x.id === id)?.name || (id ? "Unknown" : "—");
  const planName = (id) => plans.find((x) => x.id === id)?.name || (id ? "Unknown" : "—");

  const savePlan = async () => {
    if (!planForm.name.trim()) { toast.error("Plan name required"); return; }
    const payload = { ...planForm, monthly_cost: Number(planForm.monthly_cost) || 0, employer_contribution: Number(planForm.employer_contribution) || 0 };
    try {
      if (planEdit) await axios.put(`${API}/benefits/plans/${planEdit.id}`, payload);
      else await axios.post(`${API}/benefits/plans`, payload);
      toast.success(planEdit ? "Plan updated" : "Plan created"); setPlanOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delPlan = async (id) => { if (!window.confirm("Delete this plan?")) return;
    try { await axios.delete(`${API}/benefits/plans/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const saveEnr = async () => {
    if (!enrForm.employee_id || !enrForm.plan_id) { toast.error("Employee and Plan required"); return; }
    const payload = { ...enrForm, dependents: Number(enrForm.dependents) || 0 };
    if (!payload.effective_date) delete payload.effective_date;
    if (!payload.end_date) delete payload.end_date;
    try {
      if (enrEdit) await axios.put(`${API}/benefits/enrollments/${enrEdit.id}`, payload);
      else await axios.post(`${API}/benefits/enrollments`, payload);
      toast.success(enrEdit ? "Enrollment updated" : "Enrollment created"); setEnrOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delEnr = async (id) => { if (!window.confirm("Delete this enrollment?")) return;
    try { await axios.delete(`${API}/benefits/enrollments/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const filteredPlans = plans.filter((p) => !search || `${p.name} ${p.provider || ""} ${p.plan_type}`.toLowerCase().includes(search.toLowerCase()));
  const filteredEnr = enrollments.filter((e) => !search || `${empName(e.employee_id)} ${planName(e.plan_id)}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="benefits-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Employee Benefits</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage health, dental, retirement plans and employee enrollments.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={HeartPulse} label="Active Plans" value={stats.active_plans ?? 0} tint="bg-red-500/10 text-red-600" />
        <StatCard icon={Users} label="Enrollments" value={stats.active_enrollments ?? 0} tint="bg-blue-500/10 text-blue-600" />
        <StatCard icon={ShieldPlus} label="Enrolled Employees" value={stats.enrolled_employees ?? 0} tint="bg-emerald-500/10 text-emerald-600" />
        <StatCard icon={DollarSign} label="Monthly Employer Cost" value={`$${(stats.monthly_employer_cost ?? 0).toLocaleString()}`} tint="bg-amber-500/10 text-amber-600" />
        <StatCard icon={Building2} label="Plan Types" value={Object.keys(stats.enrollments_by_type || {}).length} tint="bg-purple-500/10 text-purple-600" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex justify-between items-center mb-2">
          <TabsList>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
          </TabsList>
          <div className="flex gap-2 items-center">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" /></div>
            {tab === "plans"
              ? <Button onClick={() => { setPlanEdit(null); setPlanForm(emptyPlan); setPlanOpen(true); }} data-testid="add-plan-btn"><Plus className="h-4 w-4 mr-2" />New Plan</Button>
              : <Button onClick={() => { setEnrEdit(null); setEnrForm(emptyEnr); setEnrOpen(true); }} data-testid="add-enrollment-btn"><Plus className="h-4 w-4 mr-2" />New Enrollment</Button>}
          </div>
        </div>

        <TabsContent value="plans">
          <Card><CardContent className="p-4">
            {filteredPlans.length === 0 ? <div className="py-12 text-center text-muted-foreground"><HeartPulse className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No benefit plans yet.</p></div> : (
              <Table><TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Type</TableHead><TableHead>Provider</TableHead><TableHead>Monthly Cost</TableHead><TableHead>Employer %</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{filteredPlans.map((p) => (
                <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><Badge variant="outline" className={TYPE_COLOR[p.plan_type]}>{p.plan_type}</Badge></TableCell>
                  <TableCell className="text-sm">{p.provider || "—"}</TableCell>
                  <TableCell>${Number(p.monthly_cost || 0).toLocaleString()}</TableCell>
                  <TableCell>{p.employer_contribution || 0}%</TableCell>
                  <TableCell>{p.is_active ? <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">Yes</Badge> : <Badge variant="outline">No</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setPlanEdit(p); setPlanForm({ ...emptyPlan, ...p }); setPlanOpen(true); }} data-testid={`edit-plan-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delPlan(p.id)} data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="enrollments">
          <Card><CardContent className="p-4">
            {filteredEnr.length === 0 ? <div className="py-12 text-center text-muted-foreground"><Users className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No enrollments yet.</p></div> : (
              <Table><TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Plan</TableHead><TableHead>Type</TableHead><TableHead>Effective</TableHead><TableHead>Dependents</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{filteredEnr.map((e) => (
                <TableRow key={e.id} data-testid={`enrollment-row-${e.id}`}>
                  <TableCell className="font-medium">{empName(e.employee_id)}</TableCell>
                  <TableCell>{e.plan?.name || planName(e.plan_id)}</TableCell>
                  <TableCell>{e.plan?.plan_type ? <Badge variant="outline" className={TYPE_COLOR[e.plan.plan_type]}>{e.plan.plan_type}</Badge> : "—"}</TableCell>
                  <TableCell className="text-sm">{e.effective_date ? new Date(e.effective_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>{e.dependents || 0}</TableCell>
                  <TableCell><Badge variant="outline" className={e.status === "active" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-slate-500/10 text-slate-600 border-slate-500/30"}>{e.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEnrEdit(e); setEnrForm({ ...emptyEnr, ...e }); setEnrOpen(true); }} data-testid={`edit-enrollment-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delEnr(e.id)} data-testid={`delete-enrollment-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Plan Dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{planEdit ? "Edit Plan" : "New Benefit Plan"}</DialogTitle><DialogDescription>Define a benefit plan that employees can enroll in.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plan Name *</Label><Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name-input" /></div>
              <div><Label>Type</Label>
                <Select value={planForm.plan_type} onValueChange={(v) => setPlanForm({ ...planForm, plan_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PLAN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Provider</Label><Input value={planForm.provider || ""} onChange={(e) => setPlanForm({ ...planForm, provider: e.target.value })} placeholder="Aetna, Vanguard, etc." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Monthly Cost ($)</Label><Input type="number" min="0" step="0.01" value={planForm.monthly_cost} onChange={(e) => setPlanForm({ ...planForm, monthly_cost: e.target.value })} /></div>
              <div><Label>Employer Contribution (%)</Label><Input type="number" min="0" max="100" step="0.1" value={planForm.employer_contribution} onChange={(e) => setPlanForm({ ...planForm, employer_contribution: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={planForm.description || ""} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} /></div>
            <div><Label>Coverage Details</Label><Textarea rows={3} value={planForm.coverage_details || ""} onChange={(e) => setPlanForm({ ...planForm, coverage_details: e.target.value })} placeholder="Deductibles, copays, network info..." /></div>
            <div className="flex items-center gap-2"><Switch checked={planForm.is_active} onCheckedChange={(v) => setPlanForm({ ...planForm, is_active: v })} /><Label className="m-0">Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPlanOpen(false)}>Cancel</Button><Button onClick={savePlan} data-testid="save-plan-btn">{planEdit ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrollment Dialog */}
      <Dialog open={enrOpen} onOpenChange={setEnrOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{enrEdit ? "Edit Enrollment" : "New Enrollment"}</DialogTitle><DialogDescription>Enroll an employee in a benefit plan.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employee *</Label>
                <Select value={enrForm.employee_id} onValueChange={(v) => setEnrForm({ ...enrForm, employee_id: v })}>
                  <SelectTrigger data-testid="enrollment-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Plan *</Label>
                <Select value={enrForm.plan_id} onValueChange={(v) => setEnrForm({ ...enrForm, plan_id: v })}>
                  <SelectTrigger data-testid="enrollment-plan-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{plans.filter((p) => p.is_active).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.plan_type})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Effective</Label><Input type="date" value={enrForm.effective_date || ""} onChange={(e) => setEnrForm({ ...enrForm, effective_date: e.target.value })} /></div>
              <div><Label>End Date</Label><Input type="date" value={enrForm.end_date || ""} onChange={(e) => setEnrForm({ ...enrForm, end_date: e.target.value })} /></div>
              <div><Label>Dependents</Label><Input type="number" min="0" value={enrForm.dependents} onChange={(e) => setEnrForm({ ...enrForm, dependents: e.target.value })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={enrForm.status} onValueChange={(v) => setEnrForm({ ...enrForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="ended">Ended</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={enrForm.notes || ""} onChange={(e) => setEnrForm({ ...enrForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEnrOpen(false)}>Cancel</Button><Button onClick={saveEnr} data-testid="save-enrollment-btn">{enrEdit ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
